import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { navigationItems } from '@config/navigationConfig';

const MOBILE_BREAKPOINT = 768;

export const useSideBar = ({ isAuthenticated, specialRights, editReportTemplates, onToggle, isExpanded, setActiveDropdown, location }) => {
    const { t } = useTranslation();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
    const sidebarRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isExpanded && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                onToggle(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isExpanded, onToggle]);

    useEffect(() => {
        if (isAuthenticated) {
            onToggle(false);
        }
        if (location.pathname === '/home') {
            onToggle(true);
        }
        setActiveDropdown(null);
    }, [isAuthenticated, onToggle, setActiveDropdown, location.pathname]);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
            setIsMobile(mobile);
            if (mobile) {
                onToggle(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [onToggle]);

    const filterItemsByPermissions = useCallback((items) => {
        // Ensure specialRights is an array
        const rightsArray = Array.isArray(specialRights) 
            ? specialRights 
            : (specialRights?.specialRights && Array.isArray(specialRights.specialRights))
                ? specialRights.specialRights
                : [];

        const hasEditReportTemplates = Boolean(editReportTemplates);
        
        return items
            .map(item => {
                if (item.requiresEditReportTemplates && !hasEditReportTemplates) {
                    return null;
                }

                if (!item.requiredPermissions) {
                    return {
                        ...item,
                        items: item.items ? filterItemsByPermissions(item.items) : undefined
                    };
                }
    
                const hasPermission = item.requiredPermissions.some(permission =>
                    rightsArray.includes(permission)
                );
    
                if (item.items) {
                    const filteredChildren = filterItemsByPermissions(item.items);
                    if (hasPermission || filteredChildren.length > 0) {
                        return {
                            ...item,
                            items: filteredChildren
                        };
                    }
                }
    
                return hasPermission ? item : null;
            })
            .filter(Boolean);
    }, [specialRights, editReportTemplates]);

    const handleItemClick = useCallback(() => {
        if (isMobile) {
            onToggle(false);
            setActiveDropdown(null);
        }
    }, [isMobile, onToggle, setActiveDropdown]);

    const toggleSidebar = useCallback(() => {
        onToggle(!isExpanded);
        if (isExpanded) {
            setActiveDropdown(null);
        }
    }, [isExpanded, onToggle, setActiveDropdown]);

    const authorizedItems = useMemo(() => 
        filterItemsByPermissions(navigationItems),
        [filterItemsByPermissions]
    );

    return {
        isMobile,
        handleItemClick,
        toggleSidebar,
        authorizedItems,
        sidebarRef
    };
};